// cartHelpers.ts

// Updated cart helper functions for the new cart structure with proper types

// Import clean pricing types (Phase 2)
import { CleanCartItem } from "../types/cleanPricing";

// Define interfaces for our cart structure and export them
export interface CartItem {
  cartCustomId: string;
  productId: string;
  quantity: number;
  price: number;
  finalPrice: number;
  notes?: string;
  product?: any;
  isOptimistic?: boolean; // Add isOptimistic property to fix the TypeScript error
  operationId?: string; // Add operationId for tracking optimistic updates

  // CLEAN PRICING (Phase 2 addition)
  cleanPricing?: CleanCartItem;

  // Add other fields as needed
}

export interface CartGroup {
  id: string;
  deliveryMethod: string;
  deliveryFee: number;
  subTotal: number;
  discountValue: number;
  groupTotal: number;
  delivery_fee_zone?: { id: string };
  totalWeight?: number;
  deliveryDate?: string;
  items: CartItem[];

  // CLEAN PRICING (Phase 2 addition)
  cleanTotals?: {
    originalSubtotal: number;
    customizationTotal: number;
    promotionalDiscountTotal: number;
    couponDiscountTotal: number;
    finalSubtotal: number;
    deliveryFeeTotal: number;
    groupTotal: number;
  };

  // PHASE 2.3.3: Backend-calculated group totals
  groupTotals?: {
    originalSubtotal: number;
    customizationTotal: number;
    promotionalDiscountTotal: number;
    couponDiscountTotal: number;
    finalSubtotal: number;
    deliveryFeeTotal: number;
    groupTotal: number;
  };

  // Add other fields as needed
}

export interface Cart {
  cartId: string;
  cartVersion: number;
  totalPrice: number;
  totalDeliveryFee: number;
  finalTotal: number;
  groups?: CartGroup[];
  optimisticProducts?: Array<{ product: CartItem; timestamp: number }>;

  // CLEAN PRICING (Phase 2 addition)
  cleanSummary?: {
    totals: {
      originalSubtotal: number;
      customizationTotal: number;
      promotionalDiscountTotal: number;
      couponDiscountTotal: number;
      deliveryFeeTotal: number;
      finalSubtotal: number;
      grandTotal: number;
    };
    appliedCoupons: Array<{
      code: string;
      description: string;
      totalDiscount: number;
    }>;
    appliedPromotions: Array<{
      id: number;
      name: string;
      description: string;
      totalDiscount: number;
    }>;
  };

  // Add other fields as needed
}

/**
 * Calculate the subtotal for all products in a cart group
 */
export const calculateGroupSubtotal = (group: CartGroup): number => {
  if (!group?.items || !Array.isArray(group.items)) return 0;

  return group.items.reduce((total: number, item: CartItem) => {
    return total + (parseFloat(item.finalPrice.toString()) || 0);
  }, 0);
};

/**
 * Calculate the total quantity across all cart groups
 */
export const calculateTotalQuantity = (cart: Cart): number => {
  if (!cart?.groups || !Array.isArray(cart.groups)) return 0;

  return cart.groups.reduce((total: number, group: CartGroup) => {
    if (!group.items || !Array.isArray(group.items)) return total;

    return (
      total +
      group.items.reduce((groupTotal: number, item: CartItem) => {
        return groupTotal + (item.quantity || 0);
      }, 0)
    );
  }, 0);
};

/**
 * Find a cart item by its cartCustomId
 */
export const findCartItemByCustomId = (
  cart: Cart,
  cartCustomId: string
): CartItem | null => {
  if (!cart?.groups || !Array.isArray(cart.groups)) return null;

  for (const group of cart.groups) {
    if (!group.items || !Array.isArray(group.items)) continue;

    const foundItem = group.items.find(
      (item) => item.cartCustomId === cartCustomId
    );

    if (foundItem) return foundItem;
  }

  return null;
};

/**
 * Generate a cartCustomId for consistent item identification
 */
export const generateCartCustomId = (
  productId: string,
  options: {
    groupingAttributes?: Array<{ name: string; value: string }>;
    customizableOptions?: Array<any>;
  } = {}
): string => {
  const { groupingAttributes = [], customizableOptions = [] } = options;

  // Create string from grouping attributes
  const attrString = groupingAttributes
    .map((attr) => `${attr.name}:${attr.value}`)
    .join("|");

  // Create string from customization options
  const optionsString = customizableOptions
    .filter((opt) => opt.value !== undefined && opt.value !== null)
    .map((opt) => `${opt.id}:${opt.value}`)
    .join("|");

  // Combine with productId
  let id = `${productId}`;
  if (attrString) id += `-${attrString}`;
  if (optionsString) id += `-${optionsString}`;

  return id;
};

/**
 * Group cart items by delivery zone for display or processing
 */
export const groupCartItemsByDeliveryZone = (
  cart: Cart
): Record<string, CartItem[]> => {
  const zoneGroups: Record<string, CartItem[]> = {};

  if (!cart?.groups || !Array.isArray(cart.groups)) return zoneGroups;

  cart.groups.forEach((group) => {
    const zoneId = group.delivery_fee_zone?.id || "unknown";

    if (!zoneGroups[zoneId]) {
      zoneGroups[zoneId] = [];
    }

    if (group.items && Array.isArray(group.items)) {
      zoneGroups[zoneId].push(...group.items);
    }
  });

  return zoneGroups;
};

/**
 * Calculate the latest delivery date across all cart groups
 */
export const calculateLatestDeliveryDate = (cart: Cart): Date | null => {
  if (!cart?.groups || !Array.isArray(cart.groups)) return null;

  let latestDate: Date | null = null;

  cart.groups.forEach((group) => {
    if (!group.deliveryDate) return;

    const groupDate = new Date(group.deliveryDate);

    if (!latestDate || groupDate > latestDate) {
      latestDate = groupDate;
    }
  });

  return latestDate;
};

/**
 * Filter out stale optimistic products from the cart
 * @param cart - The cart object
 * @param staleThreshold - Time in milliseconds after which an optimistic update is considered stale (default: 15000ms)
 * @returns Cart with stale optimistic products removed
 */
export const cleanupStaleOptimisticProducts = (
  cart: Cart,
  staleThreshold: number = 15000
): Cart => {
  if (!cart) return cart;

  // If no optimistic products, return cart as is
  if (!cart.optimisticProducts || cart.optimisticProducts.length === 0) {
    return cart;
  }

  const now = Date.now();
  const staleTimestamp = now - staleThreshold;

  // Filter out stale optimistic products
  const updatedOptimisticProducts = cart.optimisticProducts.filter(
    (item) => item.timestamp >= staleTimestamp
  );

  // If nothing changed, return original cart
  if (updatedOptimisticProducts.length === cart.optimisticProducts.length) {
    return cart;
  }

  // If we removed some optimistic products, clean up the cart
  const staleCartCustomIds = cart.optimisticProducts
    .filter((item) => item.timestamp < staleTimestamp)
    .map((item) => item.product.cartCustomId);

  // Remove the stale products from groups too
  const updatedGroups =
    cart.groups
      ?.map((group) => {
        if (!group.items) return group;

        return {
          ...group,
          items: group.items.filter(
            (product) =>
              !product.isOptimistic ||
              !staleCartCustomIds.includes(product.cartCustomId)
          ),
        };
      })
      .filter((group) => !group.items || group.items.length > 0) || [];

  // Return the cleaned up cart
  return {
    ...cart,
    optimisticProducts: updatedOptimisticProducts,
    groups: updatedGroups,
  };
};
